import isEqual from "lodash-es/isEqual";
import { FormProvider, useForm } from "react-hook-form";

import SharedModulesTierInfo from "components/TierInfo/SharedModulesTierInfo";
import Box from "ds/components/Box";
import Card from "ds/components/Card";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import { useFormValidations } from "hooks/useFormValidations";
import useTypedContext from "hooks/useTypedContext";
import { getDocsUrl } from "utils/getDocsUrl";

import { ModuleFormContext } from "../context";
import NewModuleFooter from "../Footer";
import { ModuleCreationWizardStep, ModuleShareFormFields } from "../types";
import AccountsField from "./AccountsField";
import Documentation from "./Documentation";

const processValuesBeforeSubmit = (values: ModuleShareFormFields): ModuleShareFormFields => {
  const sharedAccounts = values.sharedAccounts
    ?.map((account) => {
      return { value: account.value.trim() };
    })
    .filter((account) => account.value);

  return {
    ...values,
    sharedAccounts,
  };
};

const NewModuleShare = () => {
  const { currentStep, updateStepData, formData } = useTypedContext(ModuleFormContext);
  const stepData = formData[ModuleCreationWizardStep.Share];

  const builderForm = useForm<ModuleShareFormFields>({
    defaultValues: {
      ...stepData,
    },
    mode: "onChange",
  });

  const { watch } = builderForm;
  const runFormValidations = useFormValidations(builderForm);

  const newStepData = watch();

  const processStepData = () => {
    const preparedData = processValuesBeforeSubmit(newStepData);
    return updateStepData(currentStep, preparedData);
  };

  const isDataChanged =
    newStepData.sharedAccounts?.filter(({ value }) => value).length &&
    newStepData.sharedAccounts?.filter(({ value }) => value).length > 0 &&
    !isEqual(newStepData, stepData);

  return (
    <>
      <FullScreenModalBody>
        <FullScreenModalBodyHeader
          title="Share between accounts (optional)"
          description="Share the module with other Spacelift accounts. Module can only be managed from the main account."
        />

        <Box direction="column" gap="x-large" fullWidth>
          <SharedModulesTierInfo type="banner" />

          <FormProvider {...builderForm}>
            <Card variant="secondary" contentGap="large">
              <AccountsField />
            </Card>
          </FormProvider>
        </Box>
      </FullScreenModalBody>

      <NewModuleFooter
        isDataChanged={!!isDataChanged}
        processStepData={processStepData}
        // TODO module-redesign update with proper docs link
        documentationLink={getDocsUrl("/vendors/terraform/module-registry#module-availability")}
        documentationTitle="Share between accounts"
        documentationBody={<Documentation />}
        runFormValidations={runFormValidations}
      />
    </>
  );
};

export default NewModuleShare;
